import React from 'react'

import {useFormik} from 'formik'
import invariant from 'invariant'
import difference from 'lodash/difference'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'
import sortBy from 'lodash/sortBy'
import * as Yup from 'yup'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {closeModal} from '../../redux/features/modal'

import useKeyPress from '../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import {formatCurrency} from '../../libs/formatter'
import * as discountApi from '../../services/api/discount'

import {Discount, DiscountId, DiscountType, ModalMeta} from '../../types'

import Button from '../../../../commons/src/components/button'
import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import Modal from '../../../../commons/src/components/modal'
import NumberInputField from '../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import Repeatable from '../../../../commons/src/components/repeatable'

type Props = {
  meta: ModalMeta
  discounts: Array<Discount>
  type: DiscountType
  onDone?: () => void
}

type FormDiscount = Omit<Discount, 'id' | 'threshold' | 'discount'> & {
  id?: DiscountId
  threshold: string
  discount: string
}

type FormData = {
  discounts: Array<FormDiscount>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

const createFormSchema = (type: DiscountType) =>
  Yup.object().shape({
    discounts: Yup.array().of(
      Yup.object()
        .shape({
          threshold: Yup.number().label('Threshold').integer().min(0).required(),
          discount: Yup.number().label('Discount').min(0).max(100).required(),
        })
        .test('unique', 'Threshold must be unique', (value, ctx) => {
          if (
            value &&
            ctx.parent.filter((discount: typeof value) => discount.threshold === value.threshold)
              .length > 1
          ) {
            return ctx.createError({path: `${ctx.path}.threshold`})
          }
          return true
        })
    ),
  })

const discountToForm = (discount: Discount): FormDiscount => ({
  ...discount,
  threshold: `${discount.threshold}`,
  discount: `${discount.discount * 100}`,
})

const formToDiscount = (
  formDiscount: FormDiscount
): Omit<Discount, 'id'> & {
  id?: DiscountId
} => ({
  ...formDiscount,
  threshold: parseFloat(formDiscount.threshold),
  discount: parseFloat(formDiscount.discount) / 100,
})

const discountTypeNameMap: Record<DiscountType, string> = {
  [DiscountType.AMOUNT]: 'value',
  [DiscountType.QUANTITY]: 'quantity',
}

const EditDiscountsModal = ({meta, discounts = [], type, onDone = noop}: Props) => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)

  invariant(provider, 'Provider is missing.')

  const createEmptyDiscount = (): FormDiscount => ({
    type,
    provider: provider.id,
    threshold: '',
    discount: '',
  })

  const formik = useFormik<FormData>({
    initialValues: {
      discounts:
        discounts.length > 0
          ? sortBy(discounts, ['threshold']).map(discountToForm)
          : [createEmptyDiscount()],
    },
    validationSchema: createFormSchema(type),
    onSubmit: async (values, actions) => {
      const sanitizedDiscounts = values.discounts.map(formToDiscount)
      const discountsToCreate = sanitizedDiscounts.filter(discount => discount.id === undefined)
      const discountsToUpdate = sanitizedDiscounts.filter(
        (discount): discount is Discount => discount.id !== undefined
      )
      const discountIdsToRemove = difference(
        discounts.filter(discount => discount.id !== undefined).map(discount => discount.id),
        discountsToUpdate.map(discount => discount.id)
      )

      try {
        /** The order of the requests is fixed, as otherwise there will be problems with duplicate
         * key constraints if a user deletes an entry and creates a new one with the same threshold
         * value as the deleted entry. */
        if (discountIdsToRemove.length > 0) {
          await discountApi.removeMultiple(discountIdsToRemove)
        }

        if (discountsToCreate.length > 0) {
          await discountApi.createMultiple(discountsToCreate)
        }

        if (discountsToUpdate.length > 0) {
          await Promise.all(
            discountsToUpdate.map(discount => discountApi.update(discount, discount.id))
          )
        }
        onDone()
        dispatch(closeModal())
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label={`Edit ${discountTypeNameMap[type]} discounts`} size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
        <Repeatable
          min={0}
          button={<Button hierarchy="secondary">Add threshold</Button>}
          onAdd={() => {
            formik.setFieldValue('discounts', [...formik.values.discounts, createEmptyDiscount()])
          }}
          onDelete={index => {
            formik.setFieldValue(
              'discounts',
              formik.values.discounts.filter((_, i) => i !== index)
            )
          }}
        >
          {formik.values.discounts.map((discount, index) => (
            <FormRow key={index} layout="half-half">
              <FieldNote
                appearance="error"
                note={formik.submitCount > 0 && get(formik.errors, `discounts[${index}].threshold`)}
              >
                <NumberInputField
                  placeholder="Threshold"
                  prefix="≥"
                  suffix={
                    type === DiscountType.QUANTITY ? 'pcs.' : formatCurrency(provider.currency)
                  }
                  name={`discounts[${index}].threshold`}
                  value={discount.threshold}
                  appearance={
                    formik.submitCount > 0 && !!get(formik.errors, `discounts[${index}].threshold`)
                      ? 'error'
                      : undefined
                  }
                  onChange={(value, name) => formik.setFieldValue(name as string, value)}
                  onBlur={formik.handleBlur}
                />
              </FieldNote>
              <FieldNote
                appearance="error"
                note={formik.submitCount > 0 && get(formik.errors, `discounts[${index}].discount`)}
              >
                <NumberInputField
                  allowDecimals
                  placeholder="Discount"
                  suffix="%"
                  name={`discounts[${index}].discount`}
                  value={discount.discount}
                  appearance={
                    formik.submitCount > 0 && !!get(formik.errors, `discounts[${index}].discount`)
                      ? 'error'
                      : undefined
                  }
                  onChange={(value, name) => formik.setFieldValue(name as string, value)}
                  onBlur={formik.handleBlur}
                />
              </FieldNote>
            </FormRow>
          ))}
        </Repeatable>
      </form>
    </Modal>
  )
}

export default EditDiscountsModal
